import clamp from 'lodash/clamp';

// Get the scale difference between rendered image and original image
const getImageScale = (image) => ({
  x: image.naturalWidth / image.width,
  y: image.naturalHeight / image.height,
});

const getImagePxPerPercent = ({ width, height }) => ({
  x: width / 100,
  y: height / 100,
});

const convertToSharpCrop = ({ x: left, y: top, width, height }) => ({ left, top, width, height });

const convertFromSharpCrop = ({ left: x, top: y, width, height }) => ({ x, y, width, height });

/**
 * Sometimes react-image-crop returns x + width or y + height bigger than width or height of an image.
 * Which breaks image handler and it can't crop the image.
 * This function compensates that deviation
 */
const compensate = ({ crop, offsetKey, valueKey, naturalValue }) => {
  const { [offsetKey]: offset, [valueKey]: value } = crop;
  if (offset + value > naturalValue) {
    const difference = offset + value - naturalValue;
    crop[offsetKey] = Math.max(0, offset - difference);
  }
};

// We need always send crop in px units
export const scaleCrop = ({ crop, image, transform = convertToSharpCrop }) => {
  // Clamp to make sure that there won't be negative or exceeding
  const toNaturalWidth = (width, multiplier) => Math.round(clamp(width * multiplier.x, 0, image.naturalWidth));
  const toNaturalHeight = (height, multiplier) => Math.round(clamp(height * multiplier.y, 0, image.naturalHeight));

  let scaledCrop;

  if (crop.unit === '%') {
    const naturalPxToPercent = getImagePxPerPercent({ width: image.naturalWidth, height: image.naturalHeight });

    scaledCrop = {
      ...crop,
      x: toNaturalWidth(crop.x, naturalPxToPercent),
      width: toNaturalWidth(crop.width, naturalPxToPercent),
      y: toNaturalHeight(crop.y, naturalPxToPercent),
      height: toNaturalHeight(crop.height, naturalPxToPercent),
    };
  }

  if (crop.unit === 'px') {
    const scale = getImageScale(image);

    scaledCrop = {
      ...crop,
      x: toNaturalWidth(crop.x, scale),
      width: toNaturalWidth(crop.width, scale),
      y: toNaturalHeight(crop.y, scale),
      height: toNaturalHeight(crop.height, scale),
    };

    compensate({ crop: scaledCrop, offsetKey: 'x', valueKey: 'width', naturalValue: image.naturalWidth });
    compensate({ crop: scaledCrop, offsetKey: 'y', valueKey: 'height', naturalValue: image.naturalHeight });
  }

  if (!scaledCrop) throw new Error('Missing unit value');

  return transform(scaledCrop);
};

export const initCrop = ({
  initialCrop: _initialCrop,
  initialAspect,
  image,
  unit = 'px',
  transform = convertFromSharpCrop,
} = {}) => {
  const initialCrop = _initialCrop || {}; // Can be null, so default params won't work
  // TODO: check here if the crop doesn't fit image and show error
  const scale = getImageScale(image);
  const transformedInitialCrop = transform(initialCrop);

  // convert real image dimensions used in image cropper to the scaled rendered image dimensions
  const crop = {
    x: transformedInitialCrop?.x / scale.x || 0,
    width: transformedInitialCrop?.width / scale.x || 0,
    y: transformedInitialCrop?.y / scale.y || 0,
    height: transformedInitialCrop?.height / scale.y || 0,
    aspect: initialAspect,
    unit,
  };

  if (crop.unit === '%') {
    const pxPerPercent = getImagePxPerPercent(image);

    const widthToPercent = (width) => clamp(width / pxPerPercent.x, 0, 100);
    const heightToPercent = (height) => clamp(height / pxPerPercent.y, 0, 100);

    return {
      ...crop,
      x: widthToPercent(crop.x),
      width: widthToPercent(crop.width),
      y: heightToPercent(crop.y),
      height: heightToPercent(crop.height),
    };
  }

  if (crop.unit === 'px') {
    const clampWidth = (value) => clamp(value, 0, image.width);
    const clampHeight = (value) => clamp(value, 0, image.height);

    return {
      ...crop,
      x: clampWidth(crop.x),
      width: clampWidth(crop.width),
      y: clampHeight(crop.y),
      height: clampHeight(crop.height),
    };
  }

  throw new Error('Missing unit value');
};
